import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import { CheckCircle, CircleNotch, XCircle, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  const [state, setState] = useState({ phase: "polling", payment_status: null, data: null });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setState({ phase: "error", payment_status: null, data: null });
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", payment_status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "error", payment_status: data.payment_status, data });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState({ phase: "timeout", payment_status: data.payment_status, data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        setState({ phase: "error", payment_status: null, data: null });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-[#F8F9FA] flex items-center">
      <div className="max-w-2xl mx-auto px-6 w-full">
        <div className="bg-white p-10 lg:p-14 border border-[#0A1428]/10 text-center">
          {state.phase === "polling" && (
            <>
              <CircleNotch
                size={48}
                weight="duotone"
                className="mx-auto text-[#C5A059] animate-spin"
              />
              <h1
                data-testid="payment-pending-title"
                className="font-serif text-3xl text-[#0A1428] mt-6"
              >
                {t.shop.pendingTitle}
              </h1>
              <p className="text-[#4A5568] mt-3">{t.shop.pendingMessage}</p>
            </>
          )}

          {state.phase === "success" && (
            <>
              <CheckCircle
                size={56}
                weight="duotone"
                className="mx-auto text-[#C5A059]"
              />
              <p className="overline mt-6 mb-2">{t.shop.successTitle}</p>
              <h1
                data-testid="payment-success-title"
                className="font-serif text-4xl text-[#0A1428]"
              >
                {t.shop.packages[state.data?.package_id]?.title || "Grazie!"}
              </h1>
              <p className="text-[#4A5568] mt-5 leading-relaxed">
                {t.shop.successMessage}
              </p>
              <p className="text-xs uppercase tracking-widest text-[#4A5568] mt-8">
                {t.shop.orderRef}
              </p>
              <p className="font-mono text-[#0A1428] text-sm break-all mt-1">
                {sessionId}
              </p>
              <div className="flex flex-wrap gap-3 justify-center mt-10">
                <button
                  onClick={() => navigate("/")}
                  className="btn-secondary"
                  data-testid="payment-home-btn"
                >
                  {t.shop.backHome}
                </button>
                <Link to="/servizi-online" className="btn-primary">
                  {t.shop.backToShop}
                  <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </>
          )}

          {(state.phase === "error" || state.phase === "timeout") && (
            <>
              <XCircle size={56} weight="duotone" className="mx-auto text-[#0A1428]/40" />
              <h1
                data-testid="payment-error-title"
                className="font-serif text-3xl text-[#0A1428] mt-6"
              >
                {t.shop.errorTitle}
              </h1>
              <p className="text-[#4A5568] mt-3">{t.shop.errorMessage}</p>
              <Link to="/servizi-online" className="btn-primary mt-8 inline-flex">
                {t.shop.backToShop}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
